import type { Guide } from "./types.js";
import { Lead, H2, H3, P, Ul, Li, AppLink, C, Code, Callout } from "./primitives.js";

export const meta = {
  id: "dev-architecture",
  title: "Architecture overview",
  category: "For developers",
  lastUpdated: "2026-05-23",
  description: "Codebase structure, data flow, and the major abstractions. Read this first if you're new to the codebase.",
  relatedGuides: ["dev-add-channel", "dev-add-integration", "dev-conventions"],
  readTimeMin: 8,
} as const;

export function Body() {
  return (
    <>
      <Lead>
        The AlternaBe Content Machine is a TypeScript monorepo with three workspaces + a folder of MCP servers. It produces SEO-optimised Hebrew articles and distributes them across 8 channels. This guide is the map of the territory — read it before navigating the code.
      </Lead>

      <H2>Repo layout</H2>
      <Code>{`alternabe-content-machine/
├── keywords/          ← orchestration core (the heart of the app)
│   ├── src/
│   │   ├── types.ts                    ← Zod schemas + types (source of truth)
│   │   ├── db.ts                       ← SQLite open + migration runner
│   │   ├── repository.ts               ← KeywordRepository (CRUD)
│   │   ├── enrich.ts                   ← SerpAPI orchestration
│   │   ├── firecrawl.ts                ← Competitor scraping
│   │   ├── brief/                      ← Brief generation + Claude client
│   │   ├── production/                 ← Article production engine
│   │   ├── distribution/               ← Per-channel orchestrators
│   │   ├── clustering/                 ← Classifier + expander + suggestions
│   │   ├── content-db/                 ← Sanity mirror + sync
│   │   ├── discovery/                  ← URL/YT/RSS ingestion
│   │   ├── images/ + videos/           ← Asset generation
│   │   └── migrations/*.sql            ← SQLite migrations (15 applied)
│   └── package.json
├── apps/
│   ├── ui/             ← Vite + React + TS + Tailwind operator UI
│   └── ui-server/      ← Fastify + TS API server (port 4400)
├── mcps/               ← MCP servers (mostly used directly by the chat agent)
│   ├── serpapi/        ← live
│   ├── dataforseo/     ← live
│   ├── brevo/          ← live
│   ├── telegram/       ← live
│   ├── image-gen/      ← live
│   ├── video-gen/      ← live
│   └── whatsapp/, social/ ← stubs (orchestrators use direct REST instead)
├── schemas/sanity/     ← Sanity document type schemas (deployment on hold)
├── scripts/            ← Standalone test/utility scripts
├── data/               ← Local SQLite + image/video cache (.gitignored)
├── CLAUDE.md           ← Stable spec + brand voice
├── PROJECT_STATE.md    ← Volatile state — what's shipped, what's pending
└── package.json        ← Workspace root`}</Code>

      <H2>The big picture</H2>
      <P>
        One operator workflow: <strong>keyword in → article out → distribute to N channels</strong>. Everything else is in service of that.
      </P>
      <Code>{`KEYWORD
   │
   ▼  (SerpAPI)
ENRICH → serp + trends snapshot on the keyword
   │
   ▼  (Firecrawl)
SCRAPE → competitor_extracts table (markdown + headings + JSON-LD)
   │
   ▼  (Claude Sonnet/Opus)
BRIEF → Brief (outline + FAQ + semantic must-cover + sources) + internalLinks
   │
   ▼  (Claude Sonnet/Opus, heading-based)
PRODUCE → Article (H2-by-H2 with running summary + cached system prompt)
   │
   ▼  (Replicate Flux + Wan)
IMAGES + VIDEOS → data/images/<slug>/hero.png + data/videos/<slug>/short.mp4
   │
   ▼  (Per-channel orchestrators, no LLM at send time)
DISTRIBUTE → Telegram | Brevo | YouTube | FB | IG | WhatsApp | X | TikTok`}</Code>

      <H2>Core abstractions</H2>

      <H3>Zod schemas (`types.ts`)</H3>
      <P>
        Every runtime value flows through a Zod schema in <C>keywords/src/types.ts</C>. <C>KeywordRecord</C>, <C>Brief</C>, <C>Article</C>, <C>Cluster</C>, <C>CompetitorExtract</C>, <C>KeywordSuggestion</C>, <C>PublishedArticle</C> — all defined there. Repositories <C>.parse()</C> rows from SQLite into the typed record; routes <C>.safeParse()</C> request bodies. If you're tempted to define a new shape inline, add it to <C>types.ts</C> first.
      </P>

      <H3>Repository pattern</H3>
      <P>
        SQLite access is encapsulated in repos: <C>KeywordRepository</C>, <C>ClusterRepository</C>, <C>SuggestionsRepository</C>, <C>PublishedArticlesRepository</C>, etc. Each holds prepared statements + exposes <C>add</C> / <C>findById</C> / <C>list</C> / <C>upsert</C> methods. Routes never write raw SQL — they go through the repo. The <C>apps/ui-server/src/lib/repos.ts</C> module exposes lazy singletons backed by one shared connection.
      </P>

      <H3>Orchestrators</H3>
      <P>
        Per-feature workflows live in <C>keywords/src/&lt;area&gt;/</C> with a top-level entry function. Pattern: take a record, do the work, return a result + warnings. Distribution orchestrators all follow the same shape:
      </P>
      <Code>{`async function distributeToX(opts: {
  keyword: KeywordRecord;
  article: Article;
  articleMarkdown: string;
  articleUrl: string;
  // ...channel-specific options...
  dryRun?: boolean;
}): Promise<XDistributeResult>`}</Code>
      <P>
        Always support a <C>dryRun: true</C> path that previews the payload without external side effects. Always return per-step success/failure with operator-readable error messages — never throw to the route layer.
      </P>

      <H3>Integrations registry</H3>
      <P>
        Every credential in <AppLink to="credentials">Credentials</AppLink> is a single <C>Integration</C> object in <C>apps/ui-server/src/integrations/&lt;name&gt;.ts</C>. Fields (env keys), <C>test()</C> action, optional <C>actions[]</C> + <C>setupGuide</C>. Adding a new credential = one file. See the "Adding an integration" dev guide.
      </P>

      <H3>Routes</H3>
      <P>
        Fastify routes are grouped by feature under <C>apps/ui-server/src/routes/</C>. Each file exports a <C>register*Routes(app)</C> function called from <C>index.ts</C> at boot. The Pipeline action routes (Enrich, Brief, Produce, Distribute) all live in <C>pipeline-actions.ts</C>. SSE streaming for Produce is the one wrinkle worth knowing — uses <C>reply.raw.writeHead</C> + manual frame writes.
      </P>

      <H3>Background scheduler</H3>
      <P>
        Two crons in <C>apps/ui-server/src/lib/scheduler.ts</C>: discovery sweep (every minute) + Content DB sync (nightly 03:00 IL). Both are toggleable via env (<C>DISCOVERY_AUTOPOLL=off</C>, <C>CONTENT_DB_AUTOSYNC=off</C>). When we move to AWS this becomes EventBridge → Lambda; the orchestration code doesn't change.
      </P>

      <H2>Data model — SQLite</H2>
      <P>15 migrations applied. Tables (as of 2026-05-23):</P>
      <Ul>
        <Li><strong>keywords</strong> — primary entity. <C>id</C> = slugified keyword. <C>primary_keyword_id</C> FK enables cannibalization grouping (secondary → primary).</Li>
        <Li><strong>clusters</strong> — first-class topical clusters with pillar status + funnel stages + target article count.</Li>
        <Li><strong>competitor_extracts</strong> — Firecrawl results per keyword.</Li>
        <Li><strong>keyword_suggestions</strong> — cluster-expansion review queue. Operator review state preserved across re-runs.</Li>
        <Li><strong>discoveries</strong> + <strong>rss_feeds</strong> — content-discovery pipeline (URL / YT / RSS / sitemap).</Li>
        <Li><strong>distributions</strong> — per-send log (channel, kind, status, message_id, metadata).</Li>
        <Li><strong>published_articles</strong> — mirror of live Sanity content for internal-link resolution.</Li>
      </Ul>
      <P>
        Migrations are <C>*.sql</C> files in <C>keywords/src/migrations/</C> applied in alphabetical order. The runner is idempotent — safe to call on every connection. Schema changes always go through a numbered migration; never alter existing tables in place.
      </P>

      <H2>Source of truth for the spec</H2>
      <P>
        The full architecture spec lives at <C>AlternaBe_Content_Machine_v2.1.md</C> (the .docx is the canonical version; the .md is the readable mirror). Stable conventions + brand voice live at <C>CLAUDE.md</C>. Volatile state (what's shipped, what's pending, known issues) lives at <C>PROJECT_STATE.md</C>.
      </P>
      <Callout kind="tip" title="When starting a new session">
        Read <C>PROJECT_STATE.md</C> first to see current state, then <C>CLAUDE.md</C> for stable conventions, then this guide for the architecture map.
      </Callout>

      <H2>How a request flows through the stack</H2>
      <Ul>
        <Li>Operator clicks <strong>Generate brief</strong> on a keyword in the Pipeline detail panel.</Li>
        <Li>UI calls <C>api.generateBrief(keywordId)</C> → POST to <C>/api/pipeline/keywords/:id/brief</C>.</Li>
        <Li>The Fastify route in <C>pipeline-actions.ts</C> loads the keyword + extracts via repos, checks for cannibalization-secondaries, calls <C>generateBrief(...)</C> from the keywords package.</Li>
        <Li><C>generateBrief</C> builds the heuristic baseline (target word count, image plan, internal links via the resolver), then calls <C>generateLlmBrief</C> which talks to Claude via <C>resolveProvider</C> (Anthropic Direct or Bedrock).</Li>
        <Li>Result is parsed against the <C>Brief</C> Zod schema, persisted via <C>keywordRepo.update()</C>, returned to the UI.</Li>
        <Li>UI invalidates TanStack Query caches → all visible surfaces re-fetch automatically.</Li>
      </Ul>

      <H2>Where to read next</H2>
      <Ul>
        <Li><AppLink to="help?guide=dev-conventions">Codebase conventions</AppLink> — TS / ESM / Zod / Hebrew RTL / brand voice.</Li>
        <Li><AppLink to="help?guide=dev-add-channel">Adding a distribution channel</AppLink> — the most common extension point.</Li>
        <Li><AppLink to="help?guide=dev-add-integration">Adding an integration / credential</AppLink> — what files to touch.</Li>
      </Ul>
    </>
  );
}

export const guide: Guide = { meta, Body };
